import { isPlanningBufferConfigured, NO_BUFFER_SET_LABEL } from '@/utils/planningBuffer';
import { useState, useEffect } from 'react';
import { clock } from '@/utils/clock';
import { deriveLegacyShiftEnvelope } from '@/utils/technicianShiftEnvelope';
import { WorkerRoleSummary } from '@/components/roles/WorkerRoleSummary';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import * as z from 'zod';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Slider } from '@/components/ui/slider';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import { Loader2, Save, User, Clock, Wrench, TrendingUp, Calendar as CalendarIcon, ChevronLeft, ChevronRight, Plus, Trash2 } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, getDay } from 'date-fns';
import { Calendar } from '@/components/ui/calendar';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { useTutorial } from '@/contexts/TutorialContext';
import { PROFICIENCY_CATEGORIES, PROFICIENCY_WEIGHTS, calculateSkillTier as calcSkillTier, resolveSkillTier } from '@/constants/proficiencyCategories';
// CORE-E Stage B: background FACTS that justify how long an opening estimate is
// trusted. Never a proficiency, and never a strength the manager can type.
import { planCategoryBackgroundPrompts } from '@/lib/proficiency/categoryBackgroundPlan';
import { useCategoryBackground } from '@/hooks/proficiency/useCategoryBackground';
import { CategoryBackgroundFields } from './CategoryBackgroundFields';
import { cn } from '@/lib/utils';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { validateBayProficiency, checkBayConflict, syncBayAssignments } from '@/utils/bayAssignmentValidation';
import { buildTechnicianConfigurationUpdate, buildTechnicianCreationPayload } from '@/utils/technicianConfigurationPayload';
import { syncTechnicianToPassport } from '@/utils/syncTechnicianToPassport';
import { validateTechnicianHoursAgainstShopHours } from '@/utils/technicianHoursAdvisory';
import type { ShopHours } from '@/utils/shopHoursUtils';
import { useNavigate } from 'react-router-dom';
import { SliderHelpTooltip } from '@/components/ui/SliderHelpTooltip';
import { PROFICIENCY_HELP } from '@/constants/sliderHelpContent';
import { TechnicianPayCard } from '@/components/technicians/TechnicianPayCard';
import {
  EMPTY_TECHNICIAN_BACKGROUND,
  TechnicianBackgroundFields,
  hasTechnicianBackground,
  type TechnicianBackgroundValue,
} from '@/components/technicians/TechnicianBackgroundFields';
import { setTechnicianTradeBackground, useTechnicianTradeBackground } from '@/hooks/technicians/useTechnicianBackground';
import { useMechanicalTechnicianIds } from '@/hooks/useMechanicalTechnicianIds';
import { isExperienceLevel, type ExperienceLevel } from '@/lib/technicians/tradeBackground';
import { NewTechnicianInvitationField, RosterEligibilityNotice } from '@/components/technicians/RosterInvitationFields';
import { fetchShopWorkEligibilityEnforced, inviteToRosterEntry, newTechnicianInvitationProblem } from '@/lib/roles/rosterInvitation';

const technicianSchema = z.object({
  name: z.string().trim().min(1, 'Name is required').max(100, 'Name must be less than 100 characters'),
  // No `role`. `technicians.role` (Apprentice / Technician / Specialist /
  // Foreman) is a legacy compatibility column, not something a manager
  // chooses: `membership_work_roles` is the only role system. Creation gets its
  // compatibility value server-side, and an edit never writes it. See
  // LEGACY_TECHNICIAN_ROLE_DOCTRINE in technicianConfigurationPayload.ts.
  skillTier: z.coerce.number().min(1).max(10),
  shiftStart: z.string().min(1, 'Start time is required'),
  shiftEnd: z.string().min(1, 'End time is required'),
  employmentStatus: z.enum(['Active', 'Inactive', 'On Leave']),
  
  // INTEL-EFF-RANGE-001: authoritative technician efficiency range is 10–500%.
  efficiencyPercentage: z.coerce.number().min(10).max(500).default(100),
  // B-W4: unset (null) is a real state — "No buffer set", planned as 0 %. Never pre-filled with 25.
  bufferPercentage: z.number().min(0).max(100).nullable().default(null),
});

type TechnicianValues = z.infer<typeof technicianSchema>;

// Use standardized proficiency categories from constants
const bayTypes = ['Lift', 'Flat', 'Alignment', 'Specialty'];

interface TechnicianFormProps {
  technicianId: string | null;
  onSave: () => void;
  onCancel: () => void;
}

export const TechnicianForm = ({ technicianId, onSave, onCancel }: TechnicianFormProps) => {
  const { activeShop } = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { isActive, currentStepData, nextStep } = useTutorial();
  const isNew = !technicianId;
  const [isLoading, setIsLoading] = useState(!isNew);
  // Roadmap #7: prior trade background — what this technician brings. Display
  // context only; never an input to proficiency or assignment.
  const [background, setBackground] = useState<TechnicianBackgroundValue>(EMPTY_TECHNICIAN_BACKGROUND);
  const [backgroundTouched, setBackgroundTouched] = useState(false);
  const savedBackground = useTechnicianTradeBackground(activeShop?.id, technicianId);
  const mechanicalIds = useMechanicalTechnicianIds(activeShop?.id);
  // A new roster row has no work roles yet (mechanical by legacy doctrine); an
  // existing one asks the database's work-role answer (null = unknown → show).
  const backgroundApplies = isNew || !technicianId || !mechanicalIds || mechanicalIds.has(technicianId);
  useEffect(() => {
    if (backgroundTouched) return;
    const row = savedBackground.data;
    setBackground({
      experienceLevel: isExperienceLevel(row?.experience_level) ? (row!.experience_level as ExperienceLevel) : null,
      yearsProfessional: row?.years_professional ?? null,
    });
  }, [savedBackground.data, backgroundTouched]);
  const [isSaving, setIsSaving] = useState(false);
  // WS-ELIG B-E3 (CORE-W:43/:76): a NEW roster entry carries an invitation;
  // required where the shop enforces real employee eligibility.
  const [inviteEmail, setInviteEmail] = useState('');
  const [eligibilityEnforced, setEligibilityEnforced] = useState<boolean | null>(null);
  useEffect(() => {
    if (!isNew || !activeShop?.id) return;
    let cancelled = false;
    void fetchShopWorkEligibilityEnforced(activeShop.id).then((v) => { if (!cancelled) setEligibilityEnforced(v); });
    return () => { cancelled = true; };
  }, [isNew, activeShop?.id]);
  const [bayAssignments, setBayAssignments] = useState<string[]>([]); // Array of bay IDs
  const [availableBays, setAvailableBays] = useState<Array<{ id: string; name: string; bay_type: string; assigned_tech_id: string | null }>>([]);
  const [specialties, setSpecialties] = useState<string[]>([]);
  const [reassignDialog, setReassignDialog] = useState<{ open: boolean; bayId: string; bayName: string; currentTechName: string } | null>(null);
  const [proficiencies, setProficiencies] = useState<Record<string, number>>({});
  const [diagnosticWorkPreference, setDiagnosticWorkPreference] = useState<'focused' | 'balanced' | 'occasional'>('balanced');

  // CORE-E Stage B. Background is keyed by (shop, technician, category), so it
  // can only be recorded once the technician row exists — on create the id is
  // minted by the edge function after this form submits. So the prompts appear
  // on the EDIT screen, which is also where the proficiency sliders are already
  // read-only because CORE-E owns the value: the manager can no longer state the
  // score there, but they can still state the facts behind it.
  const { data: categoryBackground } = useCategoryBackground(
    activeShop?.id ?? null,
    technicianId,
  );
  const backgroundPrompts = planCategoryBackgroundPrompts(proficiencies, specialties);
  const promptedCategories = new Set(backgroundPrompts.map((p) => p.categoryKey));
  
  // Hours & Time Off state
  const [hours, setHours] = useState<Record<string, { open: string; close: string }>>({});
  const [openDays, setOpenDays] = useState<number[]>([1, 2, 3, 4, 5]); // Mon-Fri by default
  const [timeOff, setTimeOff] = useState<string[]>([]);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  
  // Breaks state
  const [recurringBreaks, setRecurringBreaks] = useState<Array<{ start: string; end: string; name: string }>>([]);
  const [nonRecurringBreaks, setNonRecurringBreaks] = useState<Array<{ date: string; start: string; end: string; name: string }>>([]);

  // Shop hours snapshot used only for the advisory rendered in the
  // "Working Days & Hours" card. Scheduling logic still reads its own
  // copy from shop_profile elsewhere; this is a UI-only side load.
  const [shopHours, setShopHours] = useState<ShopHours | null>(null);

  useEffect(() => {
    if (!activeShop?.id) return;
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from('shop_profile')
        .select('hours')
        .eq('shop_id', activeShop.id)
        .maybeSingle();
      if (cancelled) return;
      const hrs = data && (data as { hours?: unknown }).hours
        ? ((data as { hours?: unknown }).hours as ShopHours)
        : null;
      setShopHours(hrs);
    })();
    return () => { cancelled = true; };
  }, [activeShop?.id]);

  const form = useForm<TechnicianValues>({
    resolver: zodResolver(technicianSchema),
    defaultValues: {
      name: '',
      skillTier: 3,
      shiftStart: '08:00',
      shiftEnd: '17:00',
      employmentStatus: 'Active',
      efficiencyPercentage: 100,
      bufferPercentage: null,
    },
  });

  // Load available bays from shop and subscribe to realtime updates
  useEffect(() => {
    const loadBays = async () => {
      if (!activeShop) return;
      
      // Fetch bays without joining on technicians to avoid empty results
      // when assigned_tech_id references a deleted/inactive technician
      const { data, error } = await supabase
        .from('shop_bays')
        .select('id, name, bay_type, assigned_tech_id')
        .eq('shop_id', activeShop.id)
        .eq('is_active', true)
        .order('name');
      
      if (!error && data) {
        // Fetch active technician IDs separately to validate assignments
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const techQuery = supabase.from('technicians').select('id').eq('shop_id', activeShop.id) as any;
        const activeTechResult = await techQuery.eq('is_active', true);
        
        const activeTechIds = new Set((activeTechResult.data || []).map((t: any) => t.id as string));
        
        // Clear assigned_tech_id if the assigned technician is inactive/deleted
        const cleaned = data.map(bay => ({
          id: bay.id,
          name: bay.name,
          bay_type: bay.bay_type,
          assigned_tech_id: bay.assigned_tech_id && activeTechIds.has(bay.assigned_tech_id) 
            ? bay.assigned_tech_id 
            : null,
        }));
        setAvailableBays(cleaned);
      }
    };
    
    loadBays();
    
    // Phase 4: Real-time sync for bay assignments
    if (!activeShop) return;
    
    const channel = supabase
      // R37: Scope channel name with shop id (see useConfirmationAlerts).
      .channel(`shop_bays_realtime-${activeShop.id}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'shop_bays',
          filter: `shop_id=eq.${activeShop.id}`
        },
        (payload) => {
          console.log('[REALTIME] Bay assignment changed:', payload);
          loadBays();
          toast({
            title: 'Bay assignments updated',
            description: 'Bay configuration changed. Refreshing...',
            duration: 3000,
          });
        }
      )
      .subscribe((status, err) => {
        // Chronomancer R-RT-6: silent drop leaves the form showing stale
        // bay assignments during a long edit session.
        if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT' || status === 'CLOSED') {
          console.error('[TechnicianForm] shop_bays realtime status:', status, err);
        }
      });
    
    return () => {
      supabase.removeChannel(channel);
    };
  }, [activeShop]);

  useEffect(() => {
    if (!isNew && technicianId) {
      const loadTechnician = async () => {
        try {
          const { data, error } = await supabase
            .from('technicians')
            .select('*')
            .eq('id', technicianId)
            .single();

          if (error) throw error;

          if (data) {
            form.reset({
              name: data.name,
              skillTier: data.skill_tier,
              shiftStart: data.shift_start,
              shiftEnd: data.shift_end,
              employmentStatus: data.employment_status as 'Active' | 'Inactive' | 'On Leave',
              efficiencyPercentage: data.efficiency_percentage || 100,
              bufferPercentage: isPlanningBufferConfigured(data.buffer_percentage) ? data.buffer_percentage : null,
            });
            
            // Load bay assignments by ID from shop_bays
            const { data: assignedBays } = await supabase
              .from('shop_bays')
              .select('id')
              .eq('shop_id', activeShop?.id)
              .eq('assigned_tech_id', technicianId)
              .eq('is_active', true);
            
            setBayAssignments(assignedBays?.map(b => b.id) || []);
            setSpecialties(data.specialties || []);
            
            // Load proficiencies and recalculate skill tier to ensure it's accurate
            const loadedProficiencies = (data.proficiencies as Record<string, number>) || {};
            setProficiencies(loadedProficiencies);
            
            // Resolve skill tier the same way the roster cards do: derive from
            // proficiencies when the technician actually has proficiency data,
            // otherwise keep the persisted technicians.skill_tier.
            //
            // The previous unconditional calcSkillTier() call returned the
            // floor (1) for any tech with `proficiencies = {}` (every
            // manual-wizard-created technician). The form then rendered tier 1
            // while the roster card rendered the stored tier, and because
            // onSubmit writes `skill_tier: values.skillTier`, saving ANY
            // unrelated field (efficiency, buffer, employment status) silently
            // overwrote the stored tier with 1 — persistent data loss on a
            // scheduling-relevant field.
            const resolvedTier = resolveSkillTier(loadedProficiencies, data.skill_tier);
            form.setValue('skillTier', resolvedTier);
            
            // Load hours & time off data - normalize and avoid overwriting with canonical shift times
            const techHours = (data.hours as Record<string, { open?: string; close?: string }>) || {};
            const normalizedHours: Record<string, { open: string; close: string }> = {};
            Object.keys(techHours).forEach(day => {
              const dayHours = techHours[day] || {};
              normalizedHours[day] = {
                open: (dayHours.open ? dayHours.open.slice(0,5) : '08:00'),
                close: (dayHours.close ? dayHours.close.slice(0,5) : '17:00'),
              };
            });
            setHours(normalizedHours);
            const daysOpen = Object.keys(techHours).map(day => parseInt(day)).filter(day => !isNaN(day));
            // Only default to Mon-Fri for NEW technicians, preserve empty for existing ones
            setOpenDays(daysOpen.length > 0 ? daysOpen : (isNew ? [1, 2, 3, 4, 5] : []));
            setTimeOff((data.time_off as string[]) || []);
            setRecurringBreaks((data.recurring_breaks as Array<{ start: string; end: string; name: string }>) || []);
            setNonRecurringBreaks((data.non_recurring_breaks as Array<{ date: string; start: string; end: string; name: string }>) || []);
            setDiagnosticWorkPreference((data as any).diagnostic_work_preference || 'balanced');
          }
        } catch (error: any) {
          toast({
            title: 'Error loading technician',
            description: error.message,
            variant: 'destructive',
          });
          onCancel();
        } finally {
          setIsLoading(false);
        }
      };

      loadTechnician();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [technicianId, isNew]);

  const onSubmit = async (values: TechnicianValues) => {
    // PRE-SAVE VALIDATION
    const validationErrors: string[] = [];

    // WS-ELIG B-E3: an enforce shop's new entry needs the person's email (invitation).
    if (isNew) {
      const invitationProblem = newTechnicianInvitationProblem(eligibilityEnforced, inviteEmail);
      if (invitationProblem) validationErrors.push(invitationProblem);
    }

    // Validate all enabled days have valid hours (open < close)
    for (const day of openDays) {
      const dayHours = hours[day];
      if (!dayHours || !dayHours.open || !dayHours.close) {
        validationErrors.push(`${daysOfWeek[day]} is enabled but missing hours`);
        continue;
      }

      const toMinutes = (t: string) => {
        const [h, m] = t.split(':').map(Number);
        return h * 60 + (m || 0);
      };

      const openMin = toMinutes(dayHours.open);
      const closeMin = toMinutes(dayHours.close);

      if (openMin >= closeMin) {
        validationErrors.push(`${daysOfWeek[day]}: Start time (${dayHours.open}) must be before end time (${dayHours.close})`);
      }
    }

    // Validate recurring breaks fall within shift hours and start < end
    for (const [idx, breakItem] of recurringBreaks.entries()) {
      if (!breakItem.start || !breakItem.end) {
        validationErrors.push(`Recurring break #${idx + 1} is missing start or end time`);
        continue;
      }

      const toMinutes = (t: string) => {
        const [h, m] = t.split(':').map(Number);
        return h * 60 + (m || 0);
      };

      const breakStart = toMinutes(breakItem.start);
      const breakEnd = toMinutes(breakItem.end);

      if (breakStart >= breakEnd) {
        validationErrors.push(`Recurring break "${breakItem.name}": Start time must be before end time`);
      }

      // Check if break falls within any working day's shift
      let breakWithinShift = false;
      for (const day of openDays) {
        const dayHours = hours[day];
        if (dayHours) {
          const shiftStart = toMinutes(dayHours.open);
          const shiftEnd = toMinutes(dayHours.close);
          if (breakStart >= shiftStart && breakEnd <= shiftEnd) {
            breakWithinShift = true;
            break;
          }
        }
      }

      if (!breakWithinShift) {
        validationErrors.push(`Recurring break "${breakItem.name}" (${breakItem.start}-${breakItem.end}) falls outside all working shifts`);
      }
    }

    // Validate non-recurring breaks
    for (const [idx, breakItem] of nonRecurringBreaks.entries()) {
      if (!breakItem.date || !breakItem.start || !breakItem.end) {
        validationErrors.push(`Non-recurring break #${idx + 1} is missing date, start, or end time`);
        continue;
      }

      const toMinutes = (t: string) => {
        const [h, m] = t.split(':').map(Number);
        return h * 60 + (m || 0);
      };

      const breakStart = toMinutes(breakItem.start);
      const breakEnd = toMinutes(breakItem.end);

      if (breakStart >= breakEnd) {
        validationErrors.push(`Non-recurring break "${breakItem.name}" on ${breakItem.date}: Start time must be before end time`);
      }
    }

    // Warn if excessive time off (>50% of next 30 days)
    const today = new Date();
    const next30Days = eachDayOfInterval({ 
      start: today, 
      end: new Date(today.getTime() + 30 * 24 * 60 * 60 * 1000) 
    });
    const workingDaysInNext30 = next30Days.filter(date => openDays.includes(getDay(date))).length;
    const timeOffInNext30 = timeOff.filter(dateStr => {
      const date = new Date(dateStr + 'T00:00:00');
      return date >= today && date <= next30Days[next30Days.length - 1];
    }).length;

    if (workingDaysInNext30 > 0 && timeOffInNext30 / workingDaysInNext30 > 0.5) {
      validationErrors.push(`Warning: Over 50% of scheduled working days in the next 30 days are marked as time off (${timeOffInNext30}/${workingDaysInNext30} days)`);
    }

    // Show validation errors if any
    if (validationErrors.length > 0) {
      toast({
        title: 'Validation Error',
        description: (
          <div className="space-y-1">
            {validationErrors.map((err, idx) => (
              <div key={idx}>• {err}</div>
            ))}
          </div>
        ),
        variant: 'destructive',
        duration: 10000,
      });
      return;
    }

    setIsSaving(true);
    try {
      // THE FLAT COLUMNS ARE A WEEKLY SUMMARY, AND NOW THEY SAY SO.
      //
      // This derivation used to sit inline here, which meant it could not be
      // tested and nothing it produced carried any indication of what it was.
      // It computes the MODE of the Mon-Fri closes, so a technician working
      // Monday until 22:00 and Tue-Fri until 16:00 gets `shift_end = 16:00` -
      // correct as a description of a typical week, and wrong for every reader
      // that treated it as a statement about a day.
      //
      // The value is unchanged. What changed is that it has a home, a test, and
      // a `kind` field that says `typical_week`. Anything dated must ask
      // `getTechDayHours(tech, dateStr)` instead.
      const envelope = deriveLegacyShiftEnvelope(hours, {
        shift_start: values.shiftStart,
        shift_end: values.shiftEnd,
      });

      // NOTE: `bay_assignments` is intentionally OMITTED from `techData`.
      // The deployed `public.technicians` schema or one of its triggers
      // validates writes to `bay_assignments` against the `bay_type` enum
      // ('2-post', '4-post', 'scissor', etc.) — sending display strings
      // like '2-Post Lift' raised:
      //   invalid input value for enum bay_type: "2-Post Lift"
      // on every Technician profile save. The display-name mapping that
      // used to live here is identical to the CASE expression inside
      // `sync_tech_bay_assignments` (migration 20260421235000, Step 4),
      // so the RPC call below already owns the canonical write of
      // `technicians.bay_assignments`. The pre-fix client write was a
      // leftover dual-write that competed with the RPC and tripped the
      // enum validation on display strings. Removing it lets the RPC be
      // the sole authority for `bay_assignments` + `shop_bays.assigned_tech_id`,
      // which was the RPC's original design (R39 atomic-sync migration).
      const techData = {
        name: values.name,
        // No `role`: the stored legacy value is left exactly as it is on edit,
        // and creation receives its compatibility value from the server.
        skill_tier: values.skillTier,
        // Use sentinel values for legacy shift fields when hours is empty (these aren't used for scheduling)
        shift_start: envelope.shift_start,
        shift_end: envelope.shift_end,
        employment_status: values.employmentStatus,
        is_present: true, // Will be calculated based on scheduled hours
        // CORE-G: the value must carry whether it was manually set, learned
        // or imported. A person typing a number is an ASSERTION, and it has no
        // sample basis — the learned path sets both through the CORE-AN ledger.
        efficiency_percentage: values.efficiencyPercentage,
        efficiency_source: 'manual',
        efficiency_sample_size: null,
        efficiency_source_at: clock.now().toISOString(),
        buffer_percentage: values.bufferPercentage,
        specialties,
        proficiencies,
        hours,
        time_off: timeOff,
        recurring_breaks: recurringBreaks,
        non_recurring_breaks: nonRecurringBreaks,
        diagnostic_work_preference: diagnosticWorkPreference,
        shop_id: activeShop?.id || '',
      };

      let savedTechId = technicianId;

      if (isNew) {
        // Use edge function to atomically create profile + technician + proficiencies + employment history
        // Creating: the manager's opening estimate is carried, because that is
        // exactly how CORE-E's baseline prior and CORE-AN's EMA start.
        const { data, error } = await supabase.functions.invoke('create-technician-with-profile', {
          body: buildTechnicianCreationPayload(techData),
        });
        
        if (error) {
          // NOV4 P3: a refusal (e.g. DUPLICATE_ROSTER_NAME, 409) carries an actionable `detail` — show it.
          const ctx = (error as { context?: unknown }).context;
          const body = ctx instanceof Response ? await ctx.clone().json().catch(() => null) : null;
          throw new Error(body?.detail || body?.error || error.message);
        }
        if (!data?.success) throw new Error(data?.detail || data?.error || 'Failed to create technician');
        
        savedTechId = data.technician.id;
        toast({
          title: 'Technician created',
          description: `${values.name} has been added to your roster with full profile`,
        });

        // WS-ELIG B-E3 / B-E4: invite the person to THIS entry, so their
        // acceptance adopts it. A failed send never undoes the roster entry;
        // it says plainly that the person is not on the board yet.
        if (inviteEmail.trim() && activeShop?.id && savedTechId) {
          const sent = await inviteToRosterEntry({
            shopId: activeShop.id, shopName: activeShop.name, email: inviteEmail, technicianId: savedTechId,
          });
          toast(sent.ok
            ? { title: 'Invitation sent', description: `${values.name} is not on the board until they accept the invitation sent to ${inviteEmail.trim()}.` }
            : { title: 'Invitation not sent', variant: 'destructive',
                description: `${values.name} was added to the roster but could not be invited (${sent.error}). Invite them from Team Management — they are not on the board until they accept.` });
        }
      } else {
        // Editing: proficiency and efficiency are dropped. They are CORE-E's
        // projection and CORE-AN's running EMA, and this form holds whatever
        // they were when it LOADED — see technicianConfigurationPayload.ts.
        const { error } = await supabase
          .from('technicians')
          .update(buildTechnicianConfigurationUpdate(techData))
          .eq('id', technicianId);
        if (error) throw error;
        
        // Sync to passport snapshot tables (name + proficiencies + is_active)
        await syncTechnicianToPassport(technicianId);
        
        toast({
          title: 'Technician updated',
          description: `${values.name}'s profile has been saved`,
        });
      }

      // Roadmap #7: the stated background, through its one writer. Saved only
      // when entered (create) or changed (edit); a failure is reported but
      // never undoes the technician save it rides on.
      if (activeShop?.id && savedTechId && backgroundApplies
          && (isNew ? hasTechnicianBackground(background) : backgroundTouched)) {
        try {
          const res = await setTechnicianTradeBackground({
            shopId: activeShop.id,
            technicianId: savedTechId,
            experienceLevel: background.experienceLevel,
            yearsProfessional: background.yearsProfessional,
          });
          void queryClient.invalidateQueries({ queryKey: ['technician-trade-background', activeShop.id] });
          if (res.outcome === 'NOT_MECHANICAL') {
            toast({ title: 'Background not saved', description: 'Technician background applies to mechanical technicians only.' });
          }
        } catch (bgErr: any) {
          toast({ title: 'Background not saved', description: bgErr?.message ?? String(bgErr), variant: 'destructive' });
        }
      }

      // Bi-directional sync: route through the canonical syncBayAssignments
      // helper so that BOTH shop_bays.assigned_tech_id AND the denormalized
      // technicians.bay_assignments array (display-name strings) are kept in
      // step. The previous inline implementation only updated shop_bays and
      // left technicians.bay_assignments stale — proficiencyMatcher.ts:343
      // reads tech.bay_assignments for live bay-gating, so smart-assign and
      // split-option logic would silently use the pre-edit bay set after
      // every TechnicianForm save.
      if (savedTechId && activeShop?.id) {
        const syncResult = await syncBayAssignments(savedTechId, bayAssignments, activeShop.id);
        if (!syncResult.success) {
          toast({
            title: 'Bay assignments not fully saved',
            description: syncResult.error || 'Bay assignments may be out of sync. Please retry.',
            variant: 'destructive',
          });
        }
      }

      // Tutorial step 33: Auto-navigate to dashboard and advance step
      if (currentStepData?.id === 'save-tech-profile') {
        setTimeout(() => {
          navigate('/');
          nextStep();
        }, 500);
      }

      // Codex: Tech-scoped cascade after efficiency/buffer save (shop-TZ date)
      if (activeShop?.id && savedTechId) {
        const { triggerBoardCascade } = await import('@/utils/cascadeTrigger');
        const { getTodayInShopTz: getToday } = await import('@/utils/shopTime');
        // Fetch shop timezone directly since we can't use a hook in an async handler
        const { data: shopProfile } = await supabase
          .from('shop_profile')
          .select('time_zone')
          .eq('shop_id', activeShop.id)
          .maybeSingle();
        const shopTz = shopProfile?.time_zone || Intl.DateTimeFormat().resolvedOptions().timeZone;
        const todayStr = getToday(shopTz);
        triggerBoardCascade(activeShop.id, todayStr, savedTechId).catch(err =>
          console.warn('[TechnicianForm] Post-save cascade error:', err)
        );
      }

      // ── SAVE WITHOUT RELOAD ──────────────────────────────────────────
      //
      // The Dayboard's technician columns refresh on their own: `technicians`
      // is in the `supabase_realtime` publication and `Dashboard` re-reads the
      // changed row from its `postgres_changes` subscription. Shop Profile
      // refreshes through its own `loadTechnicians()`. OptiPlace and
      // `smart-assign` read the roster live at placement time and cache
      // nothing.
      //
      // React Query is the one layer that does hold on: `shop-utilization-card`
      // caches whole `technicians` rows — `hours` included — and computes the
      // shop's capacity from them, and no realtime handler invalidates it. So
      // a manager who lengthened somebody's Monday saw the board update and
      // the utilization figure keep dividing by yesterday's hours.
      //
      // Invalidated by key rather than by reloading the page: a reload would
      // hide every other stale cache instead of fixing this one.
      queryClient.invalidateQueries({ queryKey: ['shop-utilization-card'] });
      queryClient.invalidateQueries({ queryKey: ['payroll-employees'] });
      queryClient.invalidateQueries({ queryKey: ['technicians-facility'] });
      if (savedTechId) {
        queryClient.invalidateQueries({ queryKey: ['passport-live-tech'] });
        queryClient.invalidateQueries({ queryKey: ['passport-live-tech-by-profile'] });
      }

      onSave();
    } catch (error: any) {
      toast({
        title: 'Error saving technician',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const toggleBayAssignment = async (bayId: string) => {
    const isCurrentlyAssigned = bayAssignments.includes(bayId);
    
    // If unassigning, just remove it
    if (isCurrentlyAssigned) {
      setBayAssignments(prev => prev.filter(id => id !== bayId));
      return;
    }
    
    // If assigning, check for conflicts
    // CORE04-003: Run bay conflict detection for both new and existing technicians.
    // For new techs (technicianId is null), pass 'new-technician' placeholder so that
    // any existing bay owner is flagged as a conflict. This string is only used for
    // !== comparison, never as a UUID DB filter.
    if (activeShop) {
      const conflict = await checkBayConflict(bayId, technicianId || 'new-technician', activeShop.id);
      
      if (conflict.hasConflict && conflict.conflictingTech) {
        const bay = availableBays.find(b => b.id === bayId);
        setReassignDialog({
          open: true,
          bayId,
          bayName: bay?.name || 'Unknown Bay',
          currentTechName: conflict.conflictingTech.name,
        });
        return;
      }
    }
    
    // No conflict, assign normally
    setBayAssignments(prev => [...prev, bayId]);
  };
  
  const confirmReassignment = () => {
    if (reassignDialog) {
      setBayAssignments(prev => [...prev, reassignDialog.bayId]);
      setReassignDialog(null);
    }
  };
  
  const getBayProficiencyWarning = (bayType: string): string | null => {
    const validation = validateBayProficiency(bayType, proficiencies);
    return validation.warning || null;
  };

  const updateProficiency = (category: string, value: number) => {
    const oldValue = proficiencies[category] || 0;
    const isOldSpecialist = oldValue > 90;
    const isNewSpecialist = value > 90;
    
    const updatedProficiencies = { ...proficiencies, [category]: value };
    setProficiencies(updatedProficiencies);
    
    // Auto-update skill tier based on proficiencies
    const newSkillTier = calcSkillTier(updatedProficiencies);
    form.setValue('skillTier', newSkillTier);
    
    // Auto-update specialties based on proficiency
    if (isNewSpecialist && !isOldSpecialist) {
      // Just became a specialist - add to specialties and show toast
      setSpecialties(prev => prev.includes(category) ? prev : [...prev, category]);
      const technicianName = form.getValues('name') || 'Technician';
      toast({
        title: '🎉 Specialist Status Achieved!',
        description: `${technicianName} is now a specialist in ${category}!`,
        duration: 5000,
      });
    } else if (!isNewSpecialist && isOldSpecialist) {
      // No longer a specialist - remove from specialties
      setSpecialties(prev => prev.filter(s => s !== category));
    }
  };

  // Hours & Time Off functions
  const toggleDayOpen = (day: number) => {
    setOpenDays(prev => {
      const newDays = prev.includes(day) ? prev.filter(d => d !== day) : [...prev, day].sort();
      
      // Update hours accordingly
      setHours(prevHours => {
        const newHours = { ...prevHours };
        if (!newDays.includes(day)) {
          delete newHours[day];
        } else if (!newHours[day]) {
          newHours[day] = { open: '08:00', close: '17:00' };
        }
        return newHours;
      });
      
      return newDays;
    });
  };

  const updateDayHours = (day: number, field: 'open' | 'close', value: string) => {
    setHours(prev => {
      const currentHours = prev[day] || { open: '08:00', close: '17:00' };
      return {
        ...prev,
        [day]: { ...currentHours, [field]: value }
      };
    });
  };

  const toggleTimeOff = (dateStr: string) => {
    setTimeOff(prev => 
      prev.includes(dateStr) ? prev.filter(d => d !== dateStr) : [...prev, dateStr]
    );
  };

  const isTimeOff = (date: Date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return timeOff.includes(dateStr);
  };

  const isDayOpen = (date: Date) => {
    const dayOfWeek = getDay(date);
    return openDays.includes(dayOfWeek);
  };

  const getDaysInMonth = () => {
    const start = startOfMonth(currentMonth);
    const end = endOfMonth(currentMonth);
    return eachDayOfInterval({ start, end });
  };

  const addRecurringBreak = () => {
    setRecurringBreaks(prev => [...prev, { start: '12:00', end: '13:00', name: 'Lunch Break' }]);
  };

  const updateRecurringBreak = (index: number, field: string, value: string) => {
    setRecurringBreaks(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const removeRecurringBreak = (index: number) => {
    setRecurringBreaks(prev => prev.filter((_, i) => i !== index));
  };

  const addNonRecurringBreak = () => {
    setNonRecurringBreaks(prev => [...prev, { 
      date: format(new Date(), 'yyyy-MM-dd'), 
      start: '10:00', 
      end: '11:00', 
      name: 'Appointment' 
    }]);
  };

  const updateNonRecurringBreak = (index: number, field: string, value: string) => {
    setNonRecurringBreaks(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const removeNonRecurringBreak = (index: number) => {
    setNonRecurringBreaks(prev => prev.filter((_, i) => i !== index));
  };

  const daysOfWeek = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <Tabs defaultValue="general" className="space-y-6" onValueChange={(value) => {
      // Auto-advance tutorial if on click-skills-tab step and clicking skills tab
      if (isActive && currentStepData?.id === 'click-skills-tab' && value === 'skills') {
        nextStep();
      }
      // Auto-advance tutorial if on click-schedule-tab step and clicking schedule tab
      if (isActive && currentStepData?.id === 'click-schedule-tab' && value === 'schedule') {
        nextStep();
      }
      // Auto-advance tutorial if on click-hours-timeoff-tab step and clicking hours tab
      if (isActive && currentStepData?.id === 'click-hours-timeoff-tab' && value === 'hours') {
        nextStep();
      }
    }}>
      <TabsList>
        <TabsTrigger value="general" data-testid="tech-general-tab">General</TabsTrigger>
        <TabsTrigger value="skills" data-testid="tech-skills-tab">Skills & Proficiencies</TabsTrigger>
        <TabsTrigger value="schedule" data-testid="tech-schedule-tab">Bays</TabsTrigger>
        <TabsTrigger value="hours" data-testid="tech-hours-tab">Hours & Time Off</TabsTrigger>
      </TabsList>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <TabsContent value="general" className="space-y-6">
            <Card data-testid="tech-basic-info-section">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <User className="h-5 w-5" />
                  Basic Information
                </CardTitle>
                <CardDescription>Personal and employment details</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Full Name *</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="John Smith" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                {/* ONE ROLE SYSTEM. There is deliberately no roster-title
                    picker here. The legacy Apprentice / Technician /
                    Specialist / Foreman `technicians.role` is a compatibility
                    column only; what a person is and may be assigned is
                    `membership_work_roles`, shown read-only below and edited
                    in Team Management. See LEGACY_TECHNICIAN_ROLE_DOCTRINE. */}

                {/* WS-ELIG B-E3: a new entry is invited; an existing entry that
                    is not on the board (enforce shops) says so. */}
                {isNew
                  ? <NewTechnicianInvitationField enforced={eligibilityEnforced} email={inviteEmail} onEmailChange={setInviteEmail} />
                  : <RosterEligibilityNotice technicianId={technicianId} />}

                {/* The canonical roles, read-only. See WorkerRoleSummary for
                    why this surface reports rather than edits. */}
                <WorkerRoleSummary technicianId={technicianId} shopId={activeShop?.id} />
                {backgroundApplies && (
                  <div className="rounded-md border p-3" data-testid="tech-form-background">
                    <TechnicianBackgroundFields
                      value={background}
                      onChange={(v) => { setBackgroundTouched(true); setBackground(v); }}
                      idPrefix="tech-form-bg"
                    />
                  </div>
                )}
                <FormField
                  control={form.control}
                  name="skillTier"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Overall Skill Tier: {field.value}</FormLabel>
                      <FormControl>
                        <Slider
                          min={1}
                          max={10}
                          step={1}
                          value={[field.value]}
                          disabled
                          className="opacity-100"
                        />
                      </FormControl>
                      <FormDescription>Auto-calculated from weighted proficiencies (1 = Beginner, 10 = Master Technician)</FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="employmentStatus"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Employment Status *</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="Active">Active</SelectItem>
                          <SelectItem value="Inactive">Inactive</SelectItem>
                          <SelectItem value="On Leave">On Leave</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                {/* Diagnostic Work Preference (DLG) */}
                <FormItem>
                  <FormLabel>Diagnostic Work Preference</FormLabel>
                  <FormDescription>
                    Influences how diagnostic load is assessed for this technician
                  </FormDescription>
                  <Select value={diagnosticWorkPreference} onValueChange={(v) => setDiagnosticWorkPreference(v as 'focused' | 'balanced' | 'occasional')}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="focused">Focused — Prefers diagnostic-heavy assignments</SelectItem>
                      <SelectItem value="balanced">Balanced — Mix of diagnostics and mechanical</SelectItem>
                      <SelectItem value="occasional">Occasional — Prefers diagnostics in moderation</SelectItem>
                    </SelectContent>
                  </Select>
                </FormItem>

                <FormField
                  control={form.control}
                  name="efficiencyPercentage"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Efficiency Percentage: {field.value}%</FormLabel>
                      <FormDescription>
                        {isNew
                          ? 'Your opening estimate of how fast they work (50-500%). 100% = standard pace. OptiBay learns the real figure from completed work and takes over from here.'
                          : 'Learned from completed work — OptiBay keeps this up to date, so it is not edited here.'}
                      </FormDescription>
                      <FormControl>
                        <Slider
                          min={50}
                          max={500}
                          step={5}
                          value={[field.value]}
                          onValueChange={(vals) => field.onChange(vals[0])}
                          /* CORE-W requirement 7: after creation this belongs to
                             CORE-AN. A control that still moved would promise an
                             edit the save no longer makes. */
                          disabled={!isNew}
                          data-testid="technician-efficiency-slider"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="bufferPercentage"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        Buffer Percentage:{' '}
                        {field.value == null ? `${NO_BUFFER_SET_LABEL} (planned as 0%)` : `${field.value}%`}
                      </FormLabel>
                      <FormDescription>
                        Safety margin added to job time (0-100%). Higher = more conservative estimates.
                        Leave unset for no buffer.
                      </FormDescription>
                      <FormControl>
                        <Slider
                          min={0}
                          max={100}
                          step={5}
                          value={[field.value ?? 0]}
                          onValueChange={(vals) => field.onChange(vals[0])}
                        />
                      </FormControl>
                      {field.value != null && (
                        <Button type="button" variant="ghost" size="sm" onClick={() => field.onChange(null)}>
                          Clear (no buffer set)
                        </Button>
                      )}
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </CardContent>
            </Card>

            {/* Phase 10 R3 — Technician compensation editor. Hidden for non-managers
                and when labor flags are off (component handles all gating internally).
                Only mounted when editing an existing technician (need a stable id). */}
            {technicianId && <TechnicianPayCard technicianId={technicianId} />}
          </TabsContent>

          <TabsContent value="skills" className="space-y-6">
            <Card data-testid="tech-proficiencies-section">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="h-5 w-5" />
                  Proficiency Levels
                </CardTitle>
                <CardDescription>
                  {isNew
                    ? 'Your opening estimate per category (0-100%). Above 90% grants specialist status. OptiBay refines these from work the technician actually completes.'
                    : 'Demonstrated from completed work. OptiBay keeps these up to date from real outcomes, so they are shown here rather than edited.'}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {PROFICIENCY_CATEGORIES.map(category => {
                  const proficiency = proficiencies[category] || 0;
                  const isSpecialist = proficiency > 90;
                  // Roadmap #8: on an existing technician a category with no
                  // value has no OptiBay evidence yet — that is not 0%.
                  const noHistory = !isNew && (proficiencies[category] === undefined || proficiencies[category] === null);
                  
                  return (
                    <div key={category}>
                      <div className="flex items-center justify-between mb-2">
                        <label className="text-sm font-medium flex items-center gap-2">
                          {category}
                          <SliderHelpTooltip
                            title={`${category} Proficiency`}
                            description={PROFICIENCY_HELP[category]?.description || `Influences preference for ${category.toLowerCase()} work.`}
                            subcategories={PROFICIENCY_HELP[category]?.subcategories}
                          />
                          {isSpecialist && (
                            <span className="text-xs bg-gradient-to-r from-yellow-400 to-amber-500 text-black px-2 py-0.5 rounded-full font-semibold">
                              SPECIALIST
                            </span>
                          )}
                        </label>
                        {noHistory ? (
                          <span className="text-xs text-muted-foreground" data-testid={`technician-proficiency-no-history-${category}`}>
                            Not enough OptiBay history yet
                          </span>
                        ) : (
                          <span className={`text-sm font-medium ${isSpecialist ? 'text-yellow-400' : 'text-muted-foreground'}`}>
                            {proficiency}%
                          </span>
                        )}
                      </div>
                      {!noHistory && <Slider
                        min={0}
                        max={100}
                        step={5}
                        value={[proficiency]}
                        onValueChange={(vals) => updateProficiency(category, vals[0])}
                        /* CORE-W requirement 7: after creation this is CORE-E's
                           demonstrated value, projected from evidence. */
                        disabled={!isNew}
                        data-testid={`technician-proficiency-slider-${category}`}
                        className={isSpecialist ? '[&_[role=slider]]:bg-gradient-to-r [&_[role=slider]]:from-yellow-400 [&_[role=slider]]:to-amber-500 [&_[role=slider]]:border-yellow-500' : ''}
                      />}
                      {/* Stage B: only where the shop is claiming more than
                          ordinary work could substantiate, or has named this a
                          specialty. Eighteen categories, four questions each,
                          is a form nobody answers honestly. */}
                      {!isNew && activeShop?.id && technicianId && promptedCategories.has(category) && (
                        <CategoryBackgroundFields
                          shopId={activeShop.id}
                          technicianId={technicianId}
                          categoryKey={category}
                          existing={categoryBackground?.[category]}
                        />
                      )}
                    </div>
                  );
                })}
              </CardContent>
            </Card>

            {specialties.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle>Active Specialties</CardTitle>
                  <CardDescription>
                    Automatically earned when proficiency exceeds 90%
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    {specialties.map(specialty => (
                      <div 
                        key={specialty} 
                        className="px-3 py-1.5 bg-gradient-to-r from-yellow-400 to-amber-500 text-black rounded-full text-sm font-semibold"
                      >
                        {specialty}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="schedule" className="space-y-6">
            <Card data-testid="tech-bay-assignments-section">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Wrench className="h-5 w-5" />
                  Bay Assignments
                </CardTitle>
                <CardDescription>Select which bays this technician can work in</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {availableBays.length === 0 ? (
                  <div className="p-4 bg-muted/50 rounded-lg text-sm text-muted-foreground">
                    No bays configured. Create bays in the Shop Floor Editor first.
                  </div>
                ) : (
                  <div className="space-y-3">
                    {availableBays.map((bay) => {
                      const isAssigned = bayAssignments.includes(bay.id);
                      const isAssignedToOther = bay.assigned_tech_id && bay.assigned_tech_id !== technicianId;
                      const warning = getBayProficiencyWarning(bay.bay_type);
                      
                      return (
                        <div
                          key={bay.id}
                          className={cn(
                            "flex items-center gap-3 p-3 rounded-lg border transition-colors",
                            isAssigned ? "bg-primary/5 border-primary" : "bg-background border-border hover:bg-muted/50"
                          )}
                        >
                          <Checkbox
                            id={`bay-${bay.id}`}
                            checked={isAssigned}
                            onCheckedChange={() => toggleBayAssignment(bay.id)}
                          />
                          <div className="flex-1">
                            <label
                              htmlFor={`bay-${bay.id}`}
                              className="font-medium cursor-pointer flex items-center gap-2"
                            >
                              {bay.name}
                              <Badge variant="outline" className="text-xs">
                                {bay.bay_type}
                              </Badge>
                              {warning && (
                                <Badge variant="secondary" className="text-xs bg-yellow-500/10 text-yellow-700 dark:text-yellow-400">
                                  ⚠️ {warning}
                                </Badge>
                              )}
                              {isAssignedToOther && (
                                <Badge variant="secondary" className="text-xs">
                                  Already assigned
                                </Badge>
                              )}
                            </label>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="hours" className="space-y-6">
            <Card data-testid="tech-hours-timeoff-section">
              <CardHeader>
                <CardTitle>Working Days & Hours</CardTitle>
                <CardDescription>Set which days this technician works and their hours</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {(() => {
                  // Advisory-only — compare per-day tech hours against shop
                  // hours and surface messages without blocking save. Skips
                  // silently when shop hours haven't loaded yet (i.e. on a
                  // fresh new-tech form before the side-load finishes) so
                  // we don't warn against an unknown baseline.
                  const advisory = validateTechnicianHoursAgainstShopHours(
                    {
                      shift_start: form.watch('shiftStart'),
                      shift_end: form.watch('shiftEnd'),
                      hours,
                    },
                    shopHours,
                    form.watch('name'),
                  );
                  if (advisory.messages.length === 0) return null;
                  return (
                    <div
                      className="rounded border border-amber-500/40 bg-amber-500/5 p-3 text-sm"
                      data-testid="tech-hours-shop-advisory"
                    >
                      <strong className="text-amber-700">Technician hours outside shop hours:</strong>
                      <p className="text-xs text-muted-foreground mt-1">
                        This is allowed, but scheduling may create before-open or after-close availability.
                      </p>
                      <ul className="list-disc pl-5 mt-1">
                        {advisory.messages.map((m, i) => <li key={i}>{m}</li>)}
                      </ul>
                    </div>
                  );
                })()}
                {daysOfWeek.map((dayName, dayIndex) => (
                  <div key={dayIndex} className="space-y-2">
                    <div className="flex items-center gap-3">
                      <Checkbox
                        id={`day-${dayIndex}`}
                        checked={openDays.includes(dayIndex)}
                        onCheckedChange={() => toggleDayOpen(dayIndex)}
                      />
                      <label htmlFor={`day-${dayIndex}`} className="font-medium w-24">
                        {dayName}
                      </label>
                       {openDays.includes(dayIndex) && (
                        <div className="flex items-center gap-2 flex-1">
                          <Input
                            type="time"
                            value={hours[dayIndex]?.open || '08:00'}
                            onChange={(e) => {
                              const newValue = e.target.value;
                              updateDayHours(dayIndex, 'open', newValue);
                            }}
                            className="w-32"
                          />
                          <span className="text-muted-foreground">to</span>
                          <Input
                            type="time"
                            value={hours[dayIndex]?.close || '17:00'}
                            onChange={(e) => {
                              const newValue = e.target.value;
                              updateDayHours(dayIndex, 'close', newValue);
                            }}
                            className="w-32"
                          />
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>

            <Card data-testid="tech-days-unavailable-section">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CalendarIcon className="h-5 w-5" />
                  Mark Days Unavailable (Time Off)
                </CardTitle>
                <CardDescription>
                  Click dates when the technician is NOT working. Weekly 'Working Days & Hours' sets the default; this calendar sets exceptions.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="bg-muted/50 p-3 rounded-md border border-border">
                    <p className="text-sm text-muted-foreground">
                      <strong>Important:</strong> Only click dates when the technician is taking time off or unavailable. 
                      Their regular working schedule is set above in "Working Days & Hours".
                    </p>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <h3 className="font-medium">{format(currentMonth, 'MMMM yyyy')}</h3>
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          if (timeOff.length > 0 && confirm('Clear all time off dates? This cannot be undone.')) {
                            setTimeOff([]);
                          }
                        }}
                      >
                        Clear All
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setCurrentMonth(prev => new Date(prev.getFullYear(), prev.getMonth() - 1))}
                      >
                        <ChevronLeft className="h-4 w-4" />
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setCurrentMonth(prev => new Date(prev.getFullYear(), prev.getMonth() + 1))}
                      >
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-7 gap-2">
                    {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                      <div key={day} className="text-center text-sm font-medium text-muted-foreground p-2">
                        {day}
                      </div>
                    ))}
                    
                    {Array.from({ length: getDay(startOfMonth(currentMonth)) }).map((_, i) => (
                      <div key={`empty-${i}`} />
                    ))}
                    
                    {getDaysInMonth().map(date => {
                      const isOff = isTimeOff(date);
                      const isDayScheduled = isDayOpen(date);
                      const dateStr = format(date, 'yyyy-MM-dd');
                      
                      return (
                        <button
                          key={dateStr}
                          type="button"
                          onClick={() => toggleTimeOff(dateStr)}
                          className={`
                            p-2 text-sm rounded-md transition-colors
                            ${isOff 
                              ? 'bg-destructive text-destructive-foreground hover:bg-destructive/90' 
                              : isDayScheduled
                                ? 'bg-primary text-primary-foreground hover:bg-primary/90'
                                : 'bg-muted text-muted-foreground hover:bg-muted/80'
                            }
                          `}
                        >
                          {format(date, 'd')}
                        </button>
                      );
                    })}
                  </div>
                  
                  <div className="flex flex-col gap-2">
                    <p className="text-sm text-muted-foreground">
                      <span className="inline-block w-3 h-3 bg-primary rounded mr-2" />
                      Scheduled working days
                      <span className="inline-block w-3 h-3 bg-destructive rounded ml-4 mr-2" />
                      Time off / Unavailable
                      <span className="inline-block w-3 h-3 bg-muted rounded ml-4 mr-2" />
                      Not scheduled
                    </p>
                    {timeOff.length > 0 && (
                      <p className="text-sm font-medium">
                        {timeOff.length} day{timeOff.length !== 1 ? 's' : ''} marked as unavailable
                      </p>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card data-testid="tech-recurring-breaks-section">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="h-5 w-5" />
                  Recurring Breaks
                </CardTitle>
                <CardDescription>Daily breaks that occur every working day</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {recurringBreaks.map((breakItem, index) => (
                  <div key={index} className="flex items-center gap-3 p-3 border rounded-lg">
                    <Input
                      placeholder="Break name"
                      value={breakItem.name}
                      onChange={(e) => updateRecurringBreak(index, 'name', e.target.value)}
                      className="flex-1"
                    />
                    <Input
                      type="time"
                      value={breakItem.start}
                      onChange={(e) => updateRecurringBreak(index, 'start', e.target.value)}
                      className="w-32"
                    />
                    <span className="text-muted-foreground">to</span>
                    <Input
                      type="time"
                      value={breakItem.end}
                      onChange={(e) => updateRecurringBreak(index, 'end', e.target.value)}
                      className="w-32"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => removeRecurringBreak(index)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
                <Button type="button" variant="outline" onClick={addRecurringBreak} className="w-full">
                  <Plus className="mr-2 h-4 w-4" />
                  Add Recurring Break
                </Button>
              </CardContent>
            </Card>

            <Card data-testid="tech-non-recurring-breaks-section">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CalendarIcon className="h-5 w-5" />
                  Non-Recurring Breaks
                </CardTitle>
                <CardDescription>One-time breaks for appointments, etc.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {nonRecurringBreaks.map((breakItem, index) => (
                  <div key={index} className="flex items-center gap-3 p-3 border rounded-lg">
                    <Input
                      type="date"
                      value={breakItem.date}
                      onChange={(e) => updateNonRecurringBreak(index, 'date', e.target.value)}
                      className="w-40"
                    />
                    <Input
                      placeholder="Reason"
                      value={breakItem.name}
                      onChange={(e) => updateNonRecurringBreak(index, 'name', e.target.value)}
                      className="flex-1"
                    />
                    <Input
                      type="time"
                      value={breakItem.start}
                      onChange={(e) => updateNonRecurringBreak(index, 'start', e.target.value)}
                      className="w-32"
                    />
                    <span className="text-muted-foreground">to</span>
                    <Input
                      type="time"
                      value={breakItem.end}
                      onChange={(e) => updateNonRecurringBreak(index, 'end', e.target.value)}
                      className="w-32"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => removeNonRecurringBreak(index)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
                <Button type="button" variant="outline" onClick={addNonRecurringBreak} className="w-full">
                  <Plus className="mr-2 h-4 w-4" />
                  Add Non-Recurring Break
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          <div className="flex justify-end gap-3 mt-6">
            <Button type="button" variant="outline" onClick={onCancel}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSaving} data-testid="save-tech-button">
              {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              <Save className="mr-2 h-4 w-4" />
              {isNew ? 'Create Technician' : 'Save Technician'}
            </Button>
          </div>
        </form>
      </Form>
      
      {/* Reassignment Confirmation Dialog */}
      {reassignDialog && (
        <AlertDialog open={reassignDialog.open} onOpenChange={(open) => !open && setReassignDialog(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Reassign Bay?</AlertDialogTitle>
              <AlertDialogDescription>
                {reassignDialog.bayName} is currently assigned to {reassignDialog.currentTechName}. 
                Do you want to reassign this bay to {form.getValues('name') || 'this technician'}?
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel onClick={() => setReassignDialog(null)}>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={confirmReassignment}>Reassign Bay</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </Tabs>
  );
};
